import * as Yup from 'yup';

import { createEmailValidation } from '@/utils/emailValidation';
import { createZipcodeValidation } from '@/utils/zipcodeValidation';

export type CountryCode = 'MX' | 'PE' | 'CO' | 'CL';
export const ALLOWED_COUNTRIES: CountryCode[] = ['MX', 'PE', 'CO', 'CL'];
export const DEFAULT_COUNTRY: CountryCode = 'MX';
export const COUNTRY_TO_PHONE_CODE: Record<string, CountryCode> = {
  MEX: 'MX',
  CHL: 'CL',
  COL: 'CO',
  PER: 'PE',
};
export const COUNTRY_DIAL_CODES: Record<CountryCode, string> = {
  MX: '+52',
  CL: '+56',
  CO: '+57',
  PE: '+51',
};
export const MAX_PHONE_LENGTH_BY_COUNTRY: Record<CountryCode, number> = {
  MX: 13, // México
  PE: 12, // Perú
  CO: 13, // Colombia
  CL: 12, // Chile
} as const;

export const addProductSchema = Yup.object().shape({
  pictures: Yup.array().of(Yup.string()),
  csv: Yup.string(),
});

const productSchema = Yup.object().shape({
  sku: Yup.string().required('Este campo es requerido'),
  quantity: Yup.number()
    .transform((value) => {
      return value === '' || isNaN(value) ? 0 : value;
    })
    .min(1, 'El mínimo es 1')
    .typeError('Debe ser un número')
    .required('Este campo es requerido'),
});

export interface PickupData {
  packageSize: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  reference: string;
  notes: string;
}

export interface SendToWarehouseFormData {
  products: { sku: string; quantity: number }[];
  appointment: Date | null;
  requiresHomePickup: boolean;
  pickup: PickupData;
  parcels: string;
  contactPhone: string;
  shipmentType: string;
  warehouse: string;
}

const pickupSchema = Yup.object().shape({
  packageSize: Yup.string().default(''),
  address: Yup.string().default(''),
  city: Yup.string().default(''),
  country: Yup.string().default(''),
  postalCode: Yup.string().default(''),
  reference: Yup.string().optional().default(''),
  notes: Yup.string().optional().default(''),
});

export const sendToWarehouseSchema = Yup.object().shape({
  products: Yup.array().of(productSchema).required('Este campo es requerido'),
  appointment: Yup.date()
    .required('Este campo es requerido')
    .typeError('Debe ser una fecha válida')
    .nullable(),
  requiresHomePickup: Yup.boolean().default(false),
  pickup: Yup.object()
    .when('requiresHomePickup', {
      is: true,
      then: () =>
        pickupSchema.shape({
          packageSize: Yup.string().required('El tamaño del paquete es requerido'),
          address: Yup.string().required('La dirección es requerida'),
          city: Yup.string()
            .required('La ciudad es requerida')
            .test('no-whitespace-only', 'La ciudad no puede contener solo espacios', (value) => {
              return value ? value.trim().length > 0 : false;
            }),
          country: Yup.string().optional(),
          postalCode: Yup.string().test(
            'postal-code-required',
            'El código postal es requerido',
            function (value) {
              const { country } = this.parent;
              if (country === 'MEX' || country === 'COL') {
                return !!value && value.trim().length > 0;
              }
              return true;
            },
          ),
        }),
      otherwise: () => pickupSchema,
    })
    .default({
      packageSize: '',
      address: '',
      city: '',
      country: '',
      postalCode: '',
      reference: '',
      notes: '',
    }),
  parcels: Yup.string().required('Este campo es requerido'),
  shipmentType: Yup.string()
    .required('Este campo es requerido')
    .oneOf(['SPT', 'SMD'], 'Selecciona un tipo de envío válido'),
  contactPhone: Yup.string()
    .required('Este campo es requerido')
    .when('$countryCode', (countryCode, schema) => {
      if (!countryCode[0]) {
        return schema.test('has-country-code', 'Selecciona un país válido', () => false);
      }
      const expectedLength =
        MAX_PHONE_LENGTH_BY_COUNTRY[countryCode[0] as keyof typeof MAX_PHONE_LENGTH_BY_COUNTRY] ||
        13;
      const selectedCountry = countryCode[0] as CountryCode;
      const validPrefix =
        COUNTRY_DIAL_CODES[selectedCountry] || COUNTRY_DIAL_CODES[DEFAULT_COUNTRY];
      return schema
        .test(
          'valid-country-prefix',
          'El código de país no coincide con el país seleccionado',
          (value) => !value || value.startsWith(validPrefix),
        )
        .matches(/^\+\d+$/, 'El número de teléfono debe ser válido')
        .length(expectedLength, `El número debe tener ${expectedLength - 3} dígitos`);
    }),
  warehouse: Yup.string().required('Este campo es requerido'),
});

// ______________________ START Send to Client ______________________

export const personalDataSchema = Yup.object().shape({
  firstName: Yup.string().required('Este campo es requerido'),
  lastName: Yup.string().required('Este campo es requerido'),
  phone: Yup.string()
    .required('Este campo es requerido')
    .when('$country', (country, schema) => {
      if (!country[0]) {
        return schema.test('has-country-code', 'Selecciona un país válido', () => false);
      }

      const expectedLength =
        MAX_PHONE_LENGTH_BY_COUNTRY[
          country[0] as unknown as keyof typeof MAX_PHONE_LENGTH_BY_COUNTRY
        ] || 13;
      const selectedCountry = country[0] as CountryCode;
      const validPrefix =
        COUNTRY_DIAL_CODES[selectedCountry] || COUNTRY_DIAL_CODES[DEFAULT_COUNTRY];
      return schema
        .test(
          'valid-country-prefix',
          'El código de país no coincide con el país seleccionado',
          (value) => !value || value.startsWith(validPrefix),
        )
        .matches(/^\+\d+$/, 'El número de teléfono debe ser válido')
        .length(expectedLength, `El número debe tener ${expectedLength - 3} dígitos`);
    }),
  email: createEmailValidation('Debe ser un correo electrónico válido con dominio'),
});

export const addressSchema = Yup.object().shape({
  address: Yup.string().required('Este campo es requerido'),
  country: Yup.string().required('El país es requerido'),
  lat: Yup.number(),
  lng: Yup.number(),
  reference: Yup.string(),
  zipcode: createZipcodeValidation('country'),
});

export const cashOnDeliverySchema = Yup.object().shape({
  amount: Yup.number()
    .typeError('Debe ser un número')
    .default(0)
    .min(0, 'El mínimo es 0')
    .transform((value) => {
      return value === '' || isNaN(value) ? 0 : value;
    }),
});

export const paymentsSchema = Yup.object().shape({
  cashOnDelivery: cashOnDeliverySchema,
});

export const optionsSchema = Yup.object().shape({
  notes: Yup.string().optional(),
});

export const priceSchema = Yup.object().shape({
  shippingPrice: Yup.number()
    .default(0)
    .typeError('Debe ser un número')
    .min(0, 'El mínimo es 0')
    .transform((value) => {
      return value === '' || isNaN(value) ? 0 : value;
    }),
});

export const sendToClientSchema = Yup.object().shape({
  internalKey: Yup.string()
    .optional()
    .test(
      'internal-key-length',
      'El identificador debe tener al menos 5 caracteres',
      function (value) {
        if (!value || value.trim().length === 0) return true;
        return value.trim().length >= 5;
      },
    )
    .max(50, 'El identificador debe tener máximo 50 caracteres')
    .matches(/^[a-zA-Z0-9_-]*$/, 'Solo se permiten letras, números y guiones'),
  deliveryType: Yup.string()
    .default('SPT')
    .when('$hasTLM', {
      is: true,
      then: (schema) => schema.oneOf(['SPT', 'SMD', 'TLM'], 'Selecciona un tipo de envío válido'),
      otherwise: (schema) => schema.oneOf(['SPT', 'SMD'], 'Selecciona un tipo de envío válido'),
    }),
  recipient: personalDataSchema,
  destination: addressSchema,
  payments: paymentsSchema,
  options: optionsSchema,
  items: Yup.array().of(productSchema).required('Este campo es requerido'),
  price: priceSchema, // Este es el seguro? Consultar.
  warehouse: Yup.string().optional(),
});

export type SendToClientFormData = Yup.InferType<typeof sendToClientSchema>;

// ______________________ END Send to Client ______________________
